import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { QuestionService } from 'app/services/license/question.service';
import { ILicenseQuestion } from 'app/models/licenses/license-question';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-questions',
  templateUrl: './question.component.html',
  styleUrls: ['./question.component.scss']
})
export class QuestionsComponent implements OnInit {
  @Input() licenseMasterId!: number;  // The license ID passed from the parent component
  @Input() isLocked: boolean = false; // Indicates if the form is locked
  @Input() isRenewal: boolean = false;
  @Output() formValidityChange = new EventEmitter<boolean>();
  questions: ILicenseQuestion[] = [];
  questionsForm!: FormGroup;

  private questionService = inject(QuestionService);
  private fb = inject(FormBuilder);

  ngOnInit(): void {
    this.loadQuestions();
  }

  loadQuestions(): void {
    this.questionService.getLicenseQuestions(this.licenseMasterId).subscribe((questions) => {
      this.questionsForm = this.fb.group({
        questions: this.fb.array(
          questions.map((question) =>
            this.fb.group({
              id: [question.id],
              questionId: [question.questionId],
              parentId: [question.parentId],
              licenseMasterId: this.licenseMasterId,
              questionText: [question.questionText],
              yesNo: [question.yesNo ?? null, Validators.required], // Yes/No required
              comment: [question.comment ?? '', (question.yesNo && !question.questionText?.includes('outside of Oregon')) ? [Validators.required] : []] // Make comment required only if yesNo is true and not the Fieldprint question
            })
          )
        )
      });

      // Enable/disable the form after it's initialized
      if (this.isLocked) {
        this.questionsArray.disable();
      } else {
        this.questionsArray.enable();
      }

      this.questionsForm.statusChanges.subscribe(status => {
        this.checkFormValidity();
      });

      // Check initial form validity
      this.checkFormValidity();
    });
  }

  get questionsArray(): FormArray<FormGroup> {
    return this.questionsForm?.get('questions') as FormArray<FormGroup>;
  }

  onYesNoChange(index: number): void {
    if (!this.questionsArray) return;

    const questionGroup = this.questionsArray.at(index) as FormGroup;
    const commentControl = questionGroup.get('comment');

    if (questionGroup.get('yesNo')?.value === true && !questionGroup.get('questionText')?.value?.includes('outside of Oregon')) {
      // If "Yes" is selected, make comment required
      commentControl?.setValidators([Validators.required]);
    } else {
      // If "No" is selected, remove the required validator and clear comment
      commentControl?.clearValidators();
      commentControl?.setValue('');
    }

    // Important: Update validation status
    commentControl?.updateValueAndValidity();

    this.checkFormValidity();
  }

  getSubQuestionNumber(index: number): string {
    const questionGroup = this.questionsArray.at(index) as FormGroup;
    const parentId = questionGroup.get('parentId')?.value;

    let subQuestionCount = 0;

    for (let i = 0; i <= index; i++) {
      const currentGroup = this.questionsArray.at(i) as FormGroup;
      if (currentGroup.get('parentId')?.value === parentId) {
        subQuestionCount++;
      }
    }

    // Convert to letter (a, b, c, etc.)
    const letter = String.fromCharCode(96 + subQuestionCount); // 97 is 'a'
    return `${11}${letter}. `;
  }

  private checkFormValidity(): void {
    if (!this.questionsForm) {
      this.formValidityChange.emit(false);
      return;
    }

    // First check if form is valid according to Angular validators
    const isFormValid = this.questionsForm.valid;

    // Then check custom validation
    const validationErrors = this.validateQuestionsAndComments();
    const hasNoCustomErrors = validationErrors.length === 0;

    // Form is valid if both Angular validation passes AND no custom errors
    const isCompletelyValid = isFormValid && hasNoCustomErrors;
    this.formValidityChange.emit(isCompletelyValid);
  }

  validateQuestionsAndComments(): string[] {
    let errors: string[] = [];
    if (!this.questionsArray) return errors;

    for (const questionGroup of this.questionsArray.controls) {
      const question = questionGroup.value as ILicenseQuestion;

      if (question.yesNo == null) {
        errors.push("Please answer all questions before submitting.");
        return errors;
      }
      if (question.yesNo === true && !questionGroup.get('questionText')?.value?.includes('outside of Oregon') && (!question.comment || question.comment.trim() === '')) {
        errors.push("Please provide comments for all 'Yes' responses.");
        return errors;
      }
    }
    return errors;
  }

  saveQuestions(): void {
    this.checkFormValidity();
    this.questionsForm.markAllAsTouched();

    if (!this.questionsForm || this.questionsForm.invalid) {
      console.log('Form is invalid, please check required fields.');
      return;
    }

    const validationErrors = this.validateQuestionsAndComments();
    if (validationErrors.length > 0) {
      console.log('Validation errors:', validationErrors);
      return;
    }

    const questionsToSave: ILicenseQuestion[] = this.questionsArray.controls.map(group => {
      const question = group.value as ILicenseQuestion;
      question.licenseMasterId = this.licenseMasterId;
      return question;
    });

    this.questionService.updateLicenseQuestions(questionsToSave).subscribe({
      next: response => {
        if(response){
          this.checkFormValidity();
        }
      },
      error: e => {
        console.error('Failed to update questions.', e);
      }
    });
  }
}
